const normalizeRecipientId = value => (
  value == null || value === '' ? null : String(value)
);

const normalizeRecipientEmail = recipient => (
  recipient.work_email || recipient.email || ''
).trim().toLowerCase();

const getRecipientUserKeys = (recipient = {}) => [recipient.user_id, recipient.id]
  .map(normalizeRecipientId)
  .filter(id => id && !id.includes('@'))
  .map(id => `user:${id}`);

export const getRecipientIdentityKeys = (recipient = {}) => {
  const userKeys = getRecipientUserKeys(recipient);
  const email = normalizeRecipientEmail(recipient);
  return [
    ...userKeys,
    ...(email ? [`email:${email}`] : []),
  ];
};

export const isRecipientExcluded = (recipient, excludedIdentityKeys = []) => {
  const excluded = new Set(excludedIdentityKeys);
  return getRecipientIdentityKeys(recipient).some(key => excluded.has(key));
};

// Maps each broker/member identity key to the backend row id that currently assigns them
// (a commission or team-membership row). Deleting and re-creating that assignment produces a
// new row id even though the person is unchanged -- the signal used to detect a source-side
// remove-then-re-add that happened while this component wasn't mounted to observe it directly.
export const buildCommissionRowIdMap = (commissions = []) => commissions.reduce((map, commission) => {
  const brokerId = commission.broker?.user_id || commission.broker?.id;
  if (!brokerId) return map;
  getRecipientIdentityKeys({ user_id: brokerId, work_email: commission.broker.work_email })
    .forEach((key) => { map[key] = commission.id; });
  return map;
}, {});

export const buildDealMemberRowIdMap = (teamInfoResults = []) => teamInfoResults.reduce((map, entry) => {
  const rowId = entry.id;
  const users = [
    ...(entry.user ? [entry.user] : []),
    ...(entry.team?.users || []),
  ];
  users.forEach((user) => {
    if (user?.id == null) return;
    getRecipientIdentityKeys({ user_id: user.id, work_email: user.email?.work || user.work_email })
      .forEach((key) => { map[key] = rowId; });
  });
  return map;
}, {});

export const addRecipientExclusion = (excludedIdentityKeys = [], recipient = {}) => [
  ...new Set([...excludedIdentityKeys, ...getRecipientIdentityKeys(recipient)]),
];

export const removeRecipientExclusion = (excludedIdentityKeys = [], recipient = {}) => {
  const recipientKeys = new Set(getRecipientIdentityKeys(recipient));
  return excludedIdentityKeys.filter(key => !recipientKeys.has(key));
};

export const recipientsMatch = (left, right) => {
  const leftIds = getRecipientUserKeys(left);
  const rightIds = getRecipientUserKeys(right);
  if (leftIds.some(id => rightIds.includes(id))) return true;

  const leftEmail = normalizeRecipientEmail(left);
  const rightEmail = normalizeRecipientEmail(right);
  return !!leftEmail && leftEmail === rightEmail;
};

export const deduplicateRecipients = (recipients = []) => recipients.reduce((result, recipient) => {
  const existingIndex = result.findIndex(existing => recipientsMatch(existing, recipient));
  if (existingIndex === -1) return [...result, recipient];

  const existing = result[existingIndex];
  const updated = [...result];
  updated[existingIndex] = {
    ...recipient,
    ...existing,
    id: existing.id || recipient.id,
    user_id: existing.user_id || recipient.user_id,
    broker_is_lead: !!(existing.broker_is_lead || recipient.broker_is_lead),
  };
  return updated;
}, []);

export const normalizeDealTeamMembers = (teamInfoResults = []) => teamInfoResults.reduce((members, entry) => {
  const users = [
    ...(entry.user ? [entry.user] : []),
    ...(entry.team?.users || []),
  ];

  users.forEach((user) => {
    if (user?.id == null) return;
    const existingIndex = members.findIndex(member => String(member.id) === String(user.id));
    if (existingIndex === -1) {
      members.push(user);
      return;
    }

    members[existingIndex] = {
      ...members[existingIndex],
      ...user,
      is_broker: !!(members[existingIndex].is_broker || user.is_broker),
    };
  });

  return members;
}, []);

export const canonicalizeRecipientsWithDealMembers = (recipients = [], dealMembers = []) => (
  deduplicateRecipients(recipients.map((recipient) => {
    const recipientUserKeys = getRecipientUserKeys(recipient);
    const matchingMemberById = dealMembers.find(member => (
      recipientUserKeys.includes(`user:${normalizeRecipientId(member.id)}`)
      || recipientUserKeys.includes(`user:${normalizeRecipientId(member.broker_id)}`)
    ));
    if (matchingMemberById) {
      return {
        ...recipient,
        id: matchingMemberById.id,
        user_id: matchingMemberById.id,
      };
    }
    if (recipientUserKeys.length) return recipient;

    const email = normalizeRecipientEmail(recipient);
    if (!email) return recipient;
    const matchingMembers = dealMembers.filter(member => normalizeRecipientEmail({
      work_email: member.email?.work || member.work_email || '',
    }) === email);
    if (matchingMembers.length !== 1) return recipient;

    return {
      ...recipient,
      id: matchingMembers[0].id,
      user_id: matchingMembers[0].id,
    };
  }))
);

export const mergeBrokerTeamRecipients = (
  recipients = [],
  dealMembers = [],
  hasLeadProducer = false,
  excludedIdentityKeys = [],
) => {
  const canonicalRecipients = canonicalizeRecipientsWithDealMembers(recipients, dealMembers);
  if (hasLeadProducer) return canonicalRecipients;

  const brokerRecipients = dealMembers
    .filter(member => member.is_broker)
    .map(member => ({
      id: member.id,
      user_id: member.id,
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      work_email: member.email?.work || member.work_email || '',
      removable: true,
      is_broker: true,
    }))
    .filter(recipient => !isRecipientExcluded(recipient, excludedIdentityKeys));

  return deduplicateRecipients([...canonicalRecipients, ...brokerRecipients]);
};

export const resolveRecipientGreetingName = (leadProducer, users = []) => {
  const recipientLeadProducer = leadProducer || users.find(user => user.broker_is_lead);
  if (recipientLeadProducer) {
    return recipientLeadProducer.first_name || recipientLeadProducer.name || '';
  }

  const brokers = deduplicateRecipients(users.filter(user => user.is_broker));
  return brokers.length === 1 ? (brokers[0].first_name || brokers[0].name || '') : '';
};

export const replaceRecipientNamePlaceholder = (emailBody, recipientName) => {
  if (!emailBody.includes('[Recipient Name]')) return emailBody;
  return emailBody.replace(
    /[ \t]*\[Recipient Name\]/,
    recipientName ? ` ${recipientName}` : '',
  );
};

export const updateAutoRecipientGreeting = (emailBody, previousName, recipientName) => {
  if (emailBody.includes('[Recipient Name]')) {
    return replaceRecipientNamePlaceholder(emailBody, recipientName);
  }

  const previousGreeting = previousName ? `Hi ${previousName},` : 'Hi,';
  const nextGreeting = recipientName ? `Hi ${recipientName},` : 'Hi,';
  if (!emailBody.startsWith(previousGreeting)) return emailBody;

  return `${nextGreeting}${emailBody.slice(previousGreeting.length)}`;
};

const toCommissionRecipient = commission => ({
  id: commission.broker.user_id || commission.broker.id,
  user_id: commission.broker.user_id || commission.broker.id,
  first_name: commission.broker.first_name || '',
  last_name: commission.broker.last_name || '',
  work_email: commission.broker.work_email || '',
  removable: true,
  broker_is_lead: !!(commission.broker_is_lead && commission.payment_group === 1),
  is_broker: true,
});

export const reconcileCommissionRecipients = (
  recipients = [],
  commissions,
  preservedRecipients = [],
  excludedIdentityKeys = [],
) => {
  if (!Array.isArray(commissions)) return deduplicateRecipients(recipients);

  const commissionRecipients = deduplicateRecipients(
    commissions.filter(commission => commission.broker?.id || commission.broker?.user_id)
      .map(toCommissionRecipient)
      .filter(recipient => !isRecipientExcluded(recipient, excludedIdentityKeys)),
  );
  const previousLeadRecipients = recipients.filter(recipient => recipient.broker_is_lead);
  const retainedRecipients = recipients
    .filter(recipient => !isRecipientExcluded(recipient, excludedIdentityKeys))
    .filter(recipient => !recipient.broker_is_lead || commissionRecipients.some(commission => (
      recipientsMatch(recipient, commission)
    )) || preservedRecipients.some(preserved => recipientsMatch(recipient, preserved)))
    .map(recipient => ({ ...recipient, broker_is_lead: false }));

  return deduplicateRecipients([
    ...retainedRecipients,
    ...commissionRecipients,
  ]).map(recipient => ({
    ...recipient,
    ...(commissionRecipients.find(commission => recipientsMatch(recipient, commission)) || {}),
  })).filter(recipient => (
    !previousLeadRecipients.some(previousLead => recipientsMatch(previousLead, recipient))
    || commissionRecipients.some(commission => recipientsMatch(commission, recipient))
    || preservedRecipients.some(preserved => recipientsMatch(preserved, recipient))
  ));
};